import "./CreateRegistrationGroup.css";

import { Breadcrumb, Button, Form, Input, message, Select } from "antd";
import { useEffect } from "react";
import { BsTrash } from "react-icons/bs";
import { CgScreen } from "react-icons/cg";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  createRegistrationGroup,
  deleteRegistrationGroup,
  getPackages,
  getTags,
  type RegistrationGroupFormType,
  type RegistrationGroupType,
  updateRegistrationGroup,
  useAppSelector,
} from "../../../_lib/_store";

interface CreateRegistrationGroupPageProps {
  type: "create" | "edit";
}

export const CreateRegistrationGroupPage = ({
  type,
}: CreateRegistrationGroupPageProps) => {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const { isLoading } = useAppSelector((state) => state.registrationGroup);
  const { isLoading: packageLoading, packages } = useAppSelector(
    (state) => state.package,
  );
  const { isLoading: tagLoading, tags } = useAppSelector((state) => state.tag);

  const navigate = useNavigate();
  const { state } = useLocation();

  const isEdit = type === "edit";

  useEffect(() => {
    const registrationGroupObj: RegistrationGroupType =
      state?.registrationGroup;

    if (isEdit && !registrationGroupObj) {
      navigate("/registration-group");
    }
    if (isEdit) {
      form.setFieldsValue(registrationGroupObj);

      if (registrationGroupObj?.tags?.length) {
        const _tagsIds = registrationGroupObj?.tags?.map((v) => v?.id);
        form.setFieldValue("tags", _tagsIds);
      }
      if (registrationGroupObj?.packages?.length) {
        const _packagesIds = registrationGroupObj?.packages?.map((v) => v?.id);
        form.setFieldValue("packages", _packagesIds);
      }
    }
  }, [state, packages, tags]);

  const handleSubmit = async (values: RegistrationGroupFormType) => {
    const payload = {
      ...values,
      ...(isEdit ? { id: state.registrationGroup.id } : {}),
    };

    if (isEdit) {
      const res = await updateRegistrationGroup(messageApi, payload);
      if (res) {
        navigate("/registration-group/edit", {
          state: { registrationGroup: res },
          replace: true,
        });
      }
    } else {
      const res = await createRegistrationGroup(messageApi, payload);
      if (res) {
        form.resetFields();
      }
    }
  };

  const handleDelete = async () => {
    const res = await deleteRegistrationGroup(
      messageApi,
      state.registrationGroup.id,
    );
    if (res) {
      navigate("/registration-group");
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const getData = async () => {
    await getPackages(messageApi);
    await getTags(messageApi);
  };

  return (
    <>
      {contextHolder}
      <div className="createPackage">
        <Breadcrumb
          items={[
            {
              title: <Link to="/dashboard">Dashboard</Link>,
            },
            {
              title: <Link to="/registration-group">Registration Group</Link>,
            },
            {
              title: isEdit ? "Edit" : "Create new",
            },
          ]}
        />

        {!isEdit ? (
          <h2 className="title">Create new</h2>
        ) : (
          <div className="titleHeader">
            <h2 className="title">Edit</h2>
            <div className="actionButton">
              <Button
                icon={<CgScreen size={17} />}
                size="large"
                onClick={() =>
                  navigate(`/registration-group/show`, {
                    state: { registrationGroup: state?.registrationGroup },
                  })
                }
              >
                Show
              </Button>
              <Button
                icon={<BsTrash />}
                size="large"
                onClick={handleDelete}
                loading={isLoading}
              >
                Delete
              </Button>
            </div>
          </div>
        )}

        <div className="form">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{ isActive: true }}
          >
            <Form.Item
              label="Name"
              name="name"
              rules={[{ required: true, message: "Name is required" }]}
              tooltip="Name is required"
            >
              <Input size="large" />
            </Form.Item>

            <Form.Item label="Tags" name="tags">
              <Select
                mode="multiple"
                loading={tagLoading}
                size="large"
                options={tags?.map((v) => {
                  return {
                    label: v?.name,
                    value: v?.id,
                  };
                })}
              />
            </Form.Item>

            <Form.Item label="Packages" name="packages">
              <Select
                mode="multiple"
                loading={packageLoading}
                size="large"
                options={packages?.map((v) => {
                  return {
                    label: v?.name,
                    value: v?.id,
                  };
                })}
              />
            </Form.Item>

            <Form.Item
              label="Registration No"
              name="registrationNo"
              rules={[
                { required: true, message: "Registration no is required" },
              ]}
              tooltip="Registration number is required"
            >
              <Input size="large" />
            </Form.Item>

            <Form.Item
              label="Description"
              name="description"
              rules={[{ required: true, message: "Description is required" }]}
              tooltip="Description is required"
            >
              <Input size="large" />
            </Form.Item>

            <Form.Item
              label="Audit Type"
              name="auditType"
              rules={[{ required: true, message: "Audit type is required" }]}
              tooltip="Audit type is required (Verification or Certification)"
            >
              <Select
                size="large"
                options={["Verification", "Certification"]?.map((v) => {
                  return {
                    label: v,
                    value: v,
                  };
                })}
              />
            </Form.Item>

            <Form.Item label="Personal Notes" name="personalNotes">
              <Input size="large" />
            </Form.Item>

            <Form.Item label="Risk Level" name="riskLevel">
              <Select
                size="large"
                options={["Low", "Medium", "High"]?.map((v) => {
                  return {
                    label: v,
                    value: v,
                  };
                })}
              />
            </Form.Item>

            <Form.Item
              label="Ndis Standards Modules"
              name="ndisStandardsModules"
            >
              <Input size="large" />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                style={{ width: "150px" }}
                loading={isLoading}
              >
                {isEdit ? "Update" : "Save"}
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
    </>
  );
};
